import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import {
	ArrowLeft,
	ClipboardCopy,
	Lock,
	Plus,
	Trash2,
	Vote,
} from "lucide-react";
import { useId, useState } from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import { getDb } from "@/db";
import { ballots, cars, events, votes } from "@/db/schema";
import { getSession } from "@/lib/auth-session";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

// ── Server functions ────────────────────────────────────────────────

const getVotingData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventBallots = await db
			.select()
			.from(ballots)
			.where(eq(ballots.eventId, event.id));

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		// Fetch votes for all ballots with counts per car
		const allVotes: Array<{
			ballotId: number;
			carId: number;
			count: number;
		}> = [];
		for (const ballot of eventBallots) {
			const ballotVotes = await db
				.select()
				.from(votes)
				.where(eq(votes.ballotId, ballot.id));

			const counts = new Map<number, number>();
			for (const v of ballotVotes) {
				counts.set(v.carId, (counts.get(v.carId) ?? 0) + 1);
			}
			for (const [carId, count] of counts) {
				allVotes.push({ ballotId: ballot.id, carId, count });
			}
		}

		return {
			event,
			ballots: eventBallots,
			votes: allVotes,
			cars: eventCars,
		};
	});

const createBallot = createServerFn({ method: "POST" })
	.inputValidator((data: { eventId: number; name: string }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		return getDb()
			.insert(ballots)
			.values({
				eventId: data.eventId,
				name: data.name,
			})
			.returning();
	});

const closeBallot = createServerFn({ method: "POST" })
	.inputValidator((ballotId: number) => ballotId)
	.handler(async ({ data: ballotId }) => {
		await requireAdmin();
		await getDb()
			.update(ballots)
			.set({ status: "closed" })
			.where(eq(ballots.id, ballotId));
	});

const deleteBallot = createServerFn({ method: "POST" })
	.inputValidator((ballotId: number) => ballotId)
	.handler(async ({ data: ballotId }) => {
		await requireAdmin();
		const db = getDb();
		await db.delete(votes).where(eq(votes.ballotId, ballotId));
		await db.delete(ballots).where(eq(ballots.id, ballotId));
	});

// ── Route ───────────────────────────────────────────────────────────

export const Route = createFileRoute("/admin/events/$eventId/voting")({
	loader: ({ params }) => getVotingData({ data: params.eventId }),
	component: VotingPage,
});

function VotingPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const queryClient = useQueryClient();
	const [confirm, confirmDialog] = useConfirm();
	const formId = useId();

	const { data } = useQuery({
		queryKey: ["voting", params.eventId],
		queryFn: () => getVotingData({ data: params.eventId }),
		initialData,
	});

	const {
		event,
		ballots: eventBallots,
		votes: voteCounts,
		cars: carList,
	} = data;
	const [showForm, setShowForm] = useState(false);
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState<number | null>(null);

	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: ["voting", params.eventId],
		});

	async function handleCreate(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setLoading(true);
		const form = new FormData(e.currentTarget);
		await createBallot({
			data: {
				eventId: event.id,
				name: form.get("name") as string,
			},
		});
		setLoading(false);
		setShowForm(false);
		invalidate();
	}

	async function handleClose(ballotId: number) {
		const ok = await confirm({
			title: "Close Ballot",
			description: "Close this ballot? Voting will be disabled.",
			confirmLabel: "Close Ballot",
		});
		if (!ok) return;
		await closeBallot({ data: ballotId });
		invalidate();
	}

	async function handleDelete(ballotId: number) {
		const ok = await confirm({
			title: "Delete Ballot",
			description:
				"Delete this ballot and all its votes? This cannot be undone.",
			confirmLabel: "Delete",
			variant: "destructive",
		});
		if (!ok) return;
		await deleteBallot({ data: ballotId });
		invalidate();
	}

	function copyLink(ballotId: number) {
		const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
		const url = `${baseUrl}/events/${event.id}/vote/${ballotId}`;
		navigator.clipboard.writeText(url);
		setCopied(ballotId);
		setTimeout(() => setCopied(null), 2000);
	}

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			{confirmDialog}
			<div className="max-w-3xl mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<div className="flex items-center justify-between mb-6">
					<div>
						<h1 className="text-4xl text-foreground">Voting</h1>
						<p className="text-muted-foreground">
							Create ballots and share the link so attendees can vote on their phones
						</p>
					</div>
					<button
						type="button"
						onClick={() => setShowForm(!showForm)}
						className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-accent text-primary-foreground rounded-lg transition-colors text-sm font-bold uppercase"
					>
						<Plus size={14} />
						New Ballot
					</button>
				</div>

				{/* Create form */}
				{showForm && (
					<form
						onSubmit={handleCreate}
						className="bg-card rounded-xl p-4 sm:p-6 mb-6 space-y-4"
					>
						<div>
							<label htmlFor={`${formId}-name`} className="block text-sm mb-1">
								Ballot Name
							</label>
							<input
								id={`${formId}-name`}
								name="name"
								type="text"
								required
								className="w-full px-4 py-2"
								placeholder="Best Design"
							/>
						</div>
						<div className="flex gap-3">
							<button
								type="submit"
								disabled={loading}
								className="px-6 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
							>
								{loading ? "Creating..." : "Create"}
							</button>
							<button
								type="button"
								onClick={() => setShowForm(false)}
								className="px-6 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
							>
								Cancel
							</button>
						</div>
					</form>
				)}

				{/* Ballots list */}
				{eventBallots.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<Vote size={48} className="text-muted-foreground mx-auto mb-4" />
						<p className="text-foreground text-lg">No ballots created yet</p>
						<p className="text-muted-foreground text-sm mt-1">
							Create a ballot to let attendees vote on design awards
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{eventBallots.map((ballot) => {
							const ballotVoteCounts = voteCounts
								.filter((v) => v.ballotId === ballot.id)
								.sort((a, b) => b.count - a.count);
							const totalVotes = ballotVoteCounts.reduce(
								(sum, v) => sum + v.count,
								0,
							);

							return (
								<div
									key={ballot.id}
									className="bg-card/50 rounded-xl border border-border p-4"
								>
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center gap-2">
											<Vote size={18} className="text-primary" />
											<span className="text-foreground font-bold text-lg">
												{ballot.name}
											</span>
											<span
												className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${
													ballot.status === "open"
														? "bg-success/20 text-success"
														: "bg-muted text-muted-foreground"
												}`}
											>
												{ballot.status}
											</span>
											<span className="text-muted-foreground text-sm">
												{totalVotes} vote{totalVotes !== 1 ? "s" : ""}
											</span>
										</div>
										<div className="flex items-center gap-1">
											<button
												type="button"
												onClick={() => copyLink(ballot.id)}
												className="inline-flex items-center gap-1 px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
												title="Copy voting link"
											>
												<ClipboardCopy size={14} />
												{copied === ballot.id ? "Copied!" : "Link"}
											</button>
											{ballot.status === "open" && (
												<button
													type="button"
													onClick={() => handleClose(ballot.id)}
													className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
													title="Close ballot"
												>
													<Lock size={14} />
												</button>
											)}
											<button
												type="button"
												onClick={() => handleDelete(ballot.id)}
												className="p-1.5 text-destructive hover:text-destructive/80 hover:bg-muted rounded transition-colors"
												title="Delete ballot"
											>
												<Trash2 size={14} />
											</button>
										</div>
									</div>

									{/* Shareable link */}
									<div className="bg-background rounded-lg px-3 py-2 mb-3 text-sm text-muted-foreground font-mono truncate">
										/events/{event.id}/vote/{ballot.id}
									</div>

									{/* Vote results */}
									{ballotVoteCounts.length > 0 ? (
										<div className="space-y-2">
											{ballotVoteCounts.map((vc, i) => {
												const car = carList.find((c) => c.id === vc.carId);
												const pct =
													totalVotes > 0
														? Math.round((vc.count / totalVotes) * 100)
														: 0;
												return (
													<div
														key={vc.carId}
														className="flex items-center gap-3 bg-background rounded-lg px-3 py-2"
													>
														<span className="text-muted-foreground font-bold text-sm w-6 text-right">
															{i + 1}.
														</span>
														{car?.avatarUrl && (
															<img
																src={car.avatarUrl}
																alt=""
																className="w-8 h-8 rounded object-cover"
															/>
														)}
														<div className="flex-1 min-w-0">
															<p className="text-foreground font-bold text-sm truncate">
																<span className="text-primary text-xs mr-1">
																	#{car?.carNumber}
																</span>
																{car?.carName ?? car?.driverName}
															</p>
														</div>
														<div className="flex items-center gap-2">
															<div className="w-24 bg-muted rounded-full h-2 overflow-hidden">
																<div
																	className="bg-primary h-full rounded-full transition-all"
																	style={{ width: `${pct}%` }}
																/>
															</div>
															<span className="text-foreground font-bold text-sm w-10 text-right">
																{vc.count}
															</span>
														</div>
													</div>
												);
											})}
										</div>
									) : (
										<p className="text-muted-foreground text-sm">
											No votes yet
										</p>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
